import React from 'react';
import { parseFaq } from '../annuaire/designSystem';
import { getShopUrl, slugify } from '../../utils/utils';
import { adresseMasquee } from '../../annuaire/adressePublique';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const DAY_MAP = {
  lundi: 'Monday',
  mardi: 'Tuesday',
  mercredi: 'Wednesday',
  jeudi: 'Thursday',
  vendredi: 'Friday',
  samedi: 'Saturday',
  dimanche: 'Sunday',
};

/**
 * Parse opening hours from horaires.periods into OpeningHoursSpecification.
 * periods format: [{ open: { day, hour, minute }, close: { day, hour, minute } }]
 */
function buildOpeningHours(horaires) {
  if (!horaires || !horaires.periods) return [];

  const daysEn = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];

  return horaires.periods
    .filter((p) => p.open && p.close)
    .map((p) => {
      const dayName = daysEn[p.open.day] || '';
      const opens = `${String(p.open.hour || 0).padStart(2, '0')}:${String(
        p.open.minute || 0,
      ).padStart(2, '0')}`;
      const closes = `${String(p.close.hour || 0).padStart(2, '0')}:${String(
        p.close.minute || 0,
      ).padStart(2, '0')}`;

      return {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: dayName,
        opens,
        closes,
      };
    });
}

// ---------------------------------------------------------------------------
// Schema builders
// ---------------------------------------------------------------------------

function getAbsoluteShopUrl(shop) {
  const path = getShopUrl(shop);
  return `https://roulezjeunesse.com${
    path === '#' ? `/annuaire-velo/${shop.id}` : path
  }`;
}

function buildLocalBusiness(shop) {
  const details = shop.details || {};
  const seo = details.seo || {};
  const photos = details.photos || {};
  const horaires = details.horaires || {};
  const services = details.services || [];
  const avis = details.avis || [];
  const shopUrl = getAbsoluteShopUrl(shop);

  const lat = shop.latitude || shop.lat;
  const lng = shop.longitude || shop.lng;
  const phone = shop.phone || details.telephone_google || '';
  const website = shop.website || details.site_web || '';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': shopUrl,
    name: shop.name || '',
    description: seo.meta_description_seo || '',
    url: shopUrl,
  };

  if (phone) schema.telephone = phone;
  if (photos.s3_main) schema.image = photos.s3_main;

  // Address. FIXERLIGHT-268 : sans adresse publiée, ni rue ni coordonnées.
  const masquee = adresseMasquee(shop);
  const addressParts = (shop.address || '').split(/\d{5}/);
  schema.address = {
    '@type': 'PostalAddress',
    ...(masquee ? {} : { streetAddress: (addressParts[0] || '').trim() }),
    addressLocality: shop.city || '',
    postalCode: shop.postal_code || '',
    addressRegion: details.region || '',
    addressCountry: 'FR',
  };

  // Geo
  if (lat && lng && !masquee) {
    schema.geo = {
      '@type': 'GeoCoordinates',
      latitude: String(lat),
      longitude: String(lng),
    };
  }

  // Aggregate rating (only if we have a note)
  if (details.note_google) {
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: String(details.note_google),
      ratingCount: String(details.nb_avis_google || 0),
      bestRating: '5',
      worstRating: '1',
    };
  }

  // Opening hours
  const openingHours = buildOpeningHours(horaires);
  if (openingHours.length > 0) {
    schema.openingHoursSpecification = openingHours;
  }

  // Services catalog
  if (services.length > 0) {
    schema.hasOfferCatalog = {
      '@type': 'OfferCatalog',
      name: 'Services vélo',
      itemListElement: services.map((svc) => ({
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: svc,
        },
      })),
    };
  }

  // Website link
  if (website) {
    schema.sameAs = website;
  }

  // Individual reviews (optional for SEO)
  if (avis.length > 0) {
    schema.review = avis.slice(0, 5).map((r) => ({
      '@type': 'Review',
      author: {
        '@type': 'Person',
        name: r.auteur || 'Anonyme',
      },
      reviewRating: {
        '@type': 'Rating',
        ratingValue: String(r.note || 5),
        bestRating: '5',
      },
      reviewBody: r.texte || '',
      ...(r.date ? { datePublished: r.date } : {}),
    }));
  }

  return schema;
}

function buildBreadcrumbList(shop) {
  const details = shop.details || {};
  const departement = details.departement || '';
  const ville = shop.city || '';
  const nom = shop.name || '';

  const deptSlug = slugify(departement);
  const villeSlug = slugify(ville);

  const items = [
    { position: 1, name: 'Accueil', item: 'https://roulezjeunesse.com' },
    {
      position: 2,
      name: 'Annuaire',
      item: 'https://roulezjeunesse.com/annuaire-velo',
    },
  ];

  if (ville) {
    items.push({
      position: 3,
      name: ville,
      item: `https://roulezjeunesse.com/annuaire-velo/${deptSlug}/${villeSlug}`,
    });
  }

  // Last item (current page) — no "item" property, Google uses the page URL
  items.push({
    position: items.length + 1,
    name: nom,
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((i) => ({
      '@type': 'ListItem',
      ...i,
    })),
  };
}

function buildFAQPage(shop) {
  const details = shop.details || {};
  const seo = details.seo || {};
  const faqText = seo.faq_seo || '';
  const faqs = parseFaq(faqText);

  if (faqs.length === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.reponse,
      },
    })),
  };
}

// ---------------------------------------------------------------------------
// Component (renders <script> tags for Helmet)
// Escape `</` to prevent user-generated content (e.g. review text) from
// breaking out of the <script> block via a `</script>` sequence.
const safeJson = (data) => JSON.stringify(data).replace(/</g, '\\u003c');

// ---------------------------------------------------------------------------

export default function ShopJsonLd({ shop }) {
  const localBusiness = buildLocalBusiness(shop);
  const breadcrumb = buildBreadcrumbList(shop);
  const faqPage = buildFAQPage(shop);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: safeJson(localBusiness) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: safeJson(breadcrumb) }}
      />
      {faqPage && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: safeJson(faqPage) }}
        />
      )}
    </>
  );
}
